import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Platform,
  View,
  TextInput,
  StyleSheet,
  LayoutChangeEvent,
  NativeSyntheticEvent,
  TextInputFocusEventData,
} from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { CellBlurEvent, TableCellProps } from "@/components/Table/props";
import Animated, { measure, useAnimatedRef, useAnimatedStyle, useSharedValue } from "react-native-reanimated";
import { ThemedText } from "@/components/ThemedText";
import { DateTimePicker } from "@/components/Pickers/DatetimePicker";
import useContextMenu from "@/hooks/useContextMenu";
import { BooleanMenu, EnumMenu, ArrayMenu } from "../Menus";
import { ArrayChips } from "../ArrayChips";
import { resolveColor } from "../utils";
import { formatLinkedRecord } from "../types";
import { TimeBar, TimeEditorMenu } from "@/components/Editors/TimeEditor";
import { RateBar, RateEditorMenu } from "@/components/Editors/RateEditor";
import { OverrideStringBar, OverrideStringEditorMenu } from "@/components/Editors/OverrideStringEditor";
import { Button } from "../../ui/Button";
import useDateTime from "@/hooks/useDateTime";
import { useToolTip } from "@/hooks/useToolTip";
import { StatusDot } from "@/components/StatusDot";
import { IsEditingCell } from "./IsEditingCell";
// Stable JSON stringify that sorts keys (for JSONB comparison where key order varies)
const stableStringify = (obj: any): string => {
  if (obj === null || typeof obj !== "object") {
    return JSON.stringify(obj);
  }
  if (Array.isArray(obj)) {
    return "[" + obj.map(stableStringify).join(",") + "]";
  }
  const keys = Object.keys(obj).sort();
  return "{" + keys.map((k) => JSON.stringify(k) + ":" + stableStringify(obj[k])).join(",") + "}";
};

// Shallow equality check for primitive values (faster than lodash isEqual)
// Handles JSON string vs object comparison for JSONB fields (key order independent)
const shallowEqual = (a: any, b: any): boolean => {
  if (a === b) return true;

  // Normalize both values - parse JSON strings
  let normA = a;
  let normB = b;

  if (typeof a === "string") {
    try {
      normA = JSON.parse(a);
    } catch {
      // not JSON, keep as string
    }
  }
  if (typeof b === "string") {
    try {
      normB = JSON.parse(b);
    } catch {
      // not JSON, keep as string
    }
  }

  // After normalization, check equality
  if (normA === normB) return true;
  if (typeof normA !== typeof normB) return false;

  if (typeof normA === "object" && normA !== null && normB !== null) {
    // Use stable stringify for key-order-independent comparison (handles JSONB)
    return stableStringify(normA) === stableStringify(normB);
  }
  return false;
};

// Input patterns for validation
const inputPatterns = {
  integer: /^-?\d*$/,
  currency: /^-?\$?\d*\.?\d{0,2}$/,
  percentage: /^-?\d*\.?\d*$/,
};

// Format currency value for display
const formatCurrency = (value: string | number, symbol = "$"): string => {
  const numValue = typeof value === "string" ? parseFloat(value.replace(/[^\d.-]/g, "")) : value;
  if (isNaN(numValue)) return "";
  return `${symbol}${numValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Parse currency input lazily (like date parsing)
const parseCurrencyInput = (input: string): string => {
  // Remove all non-digit characters except decimal point and minus
  const cleaned = input.replace(/[^\d.-]/g, "");
  const numValue = parseFloat(cleaned);
  if (isNaN(numValue)) return "";

  // Handle lazy input like "1234" -> "12.34"
  if (!cleaned.includes(".") && cleaned.length > 2) {
    const wholePart = cleaned.slice(0, -2) || "0";
    const decimalPart = cleaned.slice(-2);
    return parseFloat(`${wholePart}.${decimalPart}`).toString();
  }

  return numValue.toString();
};

// Parse integer input
const parseIntegerInput = (input: string): string => {
  const cleaned = input.replace(/[^\d-]/g, "");
  const numValue = parseInt(cleaned);
  return isNaN(numValue) ? "" : numValue.toString();
};

// Build nested writePayload from dot-notation writeKey (e.g., "meta_data.note" -> { meta_data: { note: value } })
const buildWritePayload = (writeKey: string, value: any): Record<string, any> => {
  const parts = writeKey.split(".");
  let result: any = {};
  let current = result;
  for (let i = 0; i < parts.length - 1; i++) {
    current[parts[i]] = {};
    current = current[parts[i]];
  }
  current[parts[parts.length - 1]] = value;
  return result;
};

function TableCellComponent({
  style,
  alignment,
  editable = true,
  columnKey = "undefined",
  columnWidths,
  inputType = "string",
  onFocus,
  onBlur,
  value,
  setColumnClipping,
  rowIndex,
  rowId,
  onStartEdit,
  onStopEdit,
  validate,
  setInvalid,
  columnIndex,
  savedWidth,
  minCellWidth,
  selectedRowIds,
  isSelected,
  registerUpdate,
  unregisterUpdate,
  triggerBulkUpdate,
  readonlyCallback,
  rowData,
  writeKey,
}: TableCellProps) {
  const { colors } = useTheme();
  const { settings, toUserTimezone, fromUserTimezone } = useDateTime();
  const toolTip = useToolTip();
  const isHovering = useRef(false);
  const [isHovered, setIsHovered] = useState(false);
  const ref = useAnimatedRef();
  const cellRef = useRef<View>(null);
  const inputRef = useRef<TextInput>(null);
  const rafRef = useRef<number>(); // Track requestAnimationFrame for cleanup
  const editingValueRef = useRef<any>(null); // Capture value when editing starts to avoid re-render loops
  const contextMenu = useContextMenu();
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [isModified, setIsModified] = useState(false);

  // Sync localValue with value prop when the cell wasn't modified and not editing (handles derived fields after save)
  // Also reset modified state when refetched value matches localValue (handles bulk edit after save)
  // Uses a key comparison to avoid re-running on every render
  // Normalize value for comparison (parse JSON strings)
  const normalizedValue = useMemo(() => {
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  }, [value]);
  const valueKey = useMemo(() => stableStringify(normalizedValue), [normalizedValue]);
  const localValueKey = useMemo(() => stableStringify(localValue), [localValue]);
  useEffect(() => {
    if (!isEditing) {
      if (!isModified) {
        setLocalValue(value);
      } else if (shallowEqual(localValue, normalizedValue)) {
        // Value from database now matches our local edit - clear modified state
        // console.log(`[Cell ${columnKey}] Clearing modified - values match`);
        setIsModified(false);
      } else {
        // Debug: log why values don't match
        // console.log(`[Cell ${columnKey}] Still modified after refetch:`, {
        //   isModified,
        //   localValueKey,
        //   valueKey,
        //   localValue,
        //   normalizedValue,
        //   shallowEqualResult: shallowEqual(localValue, normalizedValue),
        // });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  // Register for bulk updates
  useEffect(() => {
    if (registerUpdate && unregisterUpdate) {
      const updateKey = `${rowId}-${columnKey}`;

      const handleBulkUpdate = (newValue: any) => {
        // For rate_object, merge only the editable rate fields, preserve row-specific fields
        if (inputType && typeof inputType === "object" && inputType.type === "rate_object") {
          setLocalValue((prev: any) => {
            const prevData = typeof prev === "string" ? JSON.parse(prev) : prev || {};
            return {
              ...prevData,
              billing_rate: newValue.billing_rate,
              payroll_rate: newValue.payroll_rate,
            };
          });
        } else if (inputType && typeof inputType === "object" && inputType.type === "override_string") {
          const osOverrideKey = inputType.overrideKey;
          setLocalValue((prev: any) => {
            const prevData = typeof prev === "string" ? JSON.parse(prev) : prev || {};
            return {
              ...prevData,
              [osOverrideKey]: newValue[osOverrideKey],
            };
          });
        } else {
          setLocalValue(newValue);
        }
        setIsModified(true);
        setIsValid(validate?.({ cell: newValue }) ?? true);
      };

      // Provide reset capability for discard
      const handleBulkReset = () => {
        setLocalValue(value); // Reset to original prop value
        setIsModified(false);
        setIsValid(true);
      };

      registerUpdate(updateKey, handleBulkUpdate, handleBulkReset);

      return () => {
        unregisterUpdate(updateKey);
      };
    }
  }, [rowId, columnKey, registerUpdate, unregisterUpdate, validate, inputType, value]);

  // Cleanup requestAnimationFrame on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const [textWidth, setTextWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isValid, setIsValid] = useState(true);
  const [_wrapperWidth, setWrapperWidth] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [position, setPosition] = useState<{
    pageX?: number;
    pageY?: number;
    width?: number;
    height?: number;
  } | null>(null);
  const overflowBuffer = 12;
  const offsetX = useSharedValue<number>(0);

  // Cache layout measurements to avoid repeated measure() calls
  const cachedLayoutRef = useRef<{
    pageX: number;
    pageY: number;
    width: number;
    height: number;
  } | null>(null);

  // Only measure layout when wrapper layout changes, not on every interaction
  const handleWrapperLayout = useCallback(
    (event: LayoutChangeEvent) => {
      setWrapperWidth(event.nativeEvent.layout.width);
      // Defer measurement to avoid blocking the UI thread
      rafRef.current = requestAnimationFrame(() => {
        const layout = measure(ref);
        if (layout) {
          cachedLayoutRef.current = {
            pageX: layout.pageX,
            pageY: layout.pageY,
            width: layout.width,
            height: layout.height,
          };
          setPosition(cachedLayoutRef.current);
        }
      });
    },
    [ref],
  );

  // Get cached layout or fallback to null
  const getCachedLayout = useCallback(() => {
    if (cachedLayoutRef.current) {
      return cachedLayoutRef.current;
    }
    // Only measure if cache is empty (first time)
    const layout = measure(ref);
    if (layout) {
      cachedLayoutRef.current = {
        pageX: layout.pageX,
        pageY: layout.pageY,
        width: layout.width,
        height: layout.height,
      };
    }
    return cachedLayoutRef.current;
  }, [ref]);
  const handleReset = useCallback(() => {
    setLocalValue(value);
  }, [value]);

  const handlePointerDown = useCallback(() => {
    // Use cached layout instead of measuring
    const layout = getCachedLayout();
    if (layout) {
      setPosition(layout);
    }
  }, [getCachedLayout]);

  const handlePointerUp = useCallback(() => {
    // Invoke readonly callback if provided
    if (readonlyCallback && columnKey) {
      readonlyCallback(columnKey, value, rowData);
    }
  }, [readonlyCallback, columnKey, value, rowData]);

  // Shared function to trigger bulk edit logic (defined before createContextMenuContent)
  const triggerBulkEditIfNeeded = useCallback(
    (newValue: any, originalValue: any = value, writePayload?: Record<string, any>) => {
      // Always trigger bulk edit for selected rows, regardless of whether it's "modified" from original
      // This allows users to pick different values multiple times
      if (isSelected && selectedRowIds && selectedRowIds.length > 1 && triggerBulkUpdate) {
        triggerBulkUpdate(columnKey, newValue, selectedRowIds, writePayload);
      }
      const modified = !shallowEqual(originalValue, newValue);
      return modified;
    },
    [isSelected, selectedRowIds, triggerBulkUpdate, columnKey, value],
  );

  // Create context menu content factory (lazy load on demand)
  const createContextMenuContent = useCallback(
    (inputTypeStr: string, currentLayout: any) => {
      contextMenu.setIgnoreRef(inputRef);
      contextMenu.setTriggerElement?.({
        x: currentLayout.pageX,
        y: currentLayout.pageY,
        width: currentLayout.width,
        height: currentLayout.height,
      });
      contextMenu.setLocation({
        y: currentLayout.pageY + currentLayout.height,
        x: currentLayout.pageX + offsetX.value,
      });

      switch (inputTypeStr) {
        case "string":
          break;
        case "boolean":
          const booleanConfig = typeof inputType === "object" && inputType.type === "boolean" ? inputType : null;
          contextMenu.setMenuContent(
            <BooleanMenu
              style={{ width: columnWidths[columnKey].value - 20, maxWidth: 150 }}
              trueColor={booleanConfig?.trueColor}
              falseColor={booleanConfig?.falseColor}
              onSet={(val) => {
                setLocalValue(val);
                // Trigger bulk edit logic immediately
                const boolWritePayload = writeKey ? buildWritePayload(writeKey, val) : { [columnKey]: val };
                triggerBulkEditIfNeeded(val, value, boolWritePayload);
                // Trigger onBlur callbacks manually without using handleBlur
                const modified = !shallowEqual(value, val);
                setIsModified(modified);
                const onBlurProps: CellBlurEvent = {
                  rowId,
                  columnKey,
                  original: value,
                  current: val,
                  modified,
                  valid: validate?.({ cell: val }) ?? true,
                  reset: handleReset,
                  writePayload: boolWritePayload,
                };
                onBlur?.(onBlurProps);
                setIsValid(validate?.({ cell: val }) ?? true);
                onStopEdit?.();
                setIsEditing(false);
                contextMenu.setVisible(false);
              }}
            />,
          );
          break;
        case "date":
          const dateValue = localValue ? new Date(localValue) : new Date();
          contextMenu.setMenuContent(
            <DateTimePicker
              date={dateValue}
              hideTime={true}
              onPick={({ date: newDate }) => {
                const utcDate = fromUserTimezone(newDate);
                setLocalValue(utcDate.toISOString());
                const dateWritePayload = writeKey
                  ? buildWritePayload(writeKey, utcDate.toISOString())
                  : { [columnKey]: utcDate.toISOString() };
                triggerBulkEditIfNeeded(utcDate.toISOString(), value, dateWritePayload);
                const modified = !shallowEqual(value, utcDate.toISOString());
                setIsModified(modified);
                const onBlurProps: CellBlurEvent = {
                  rowId,
                  columnKey,
                  original: value,
                  current: utcDate.toISOString(),
                  modified,
                  valid: validate?.({ cell: utcDate.toISOString() }) ?? true,
                  reset: handleReset,
                  writePayload: dateWritePayload,
                };
                onBlur?.(onBlurProps);
                setIsValid(validate?.({ cell: utcDate.toISOString() }) ?? true);
                onStopEdit?.();
                setIsEditing(false);
                contextMenu.setVisible(false);
              }}
            />,
          );
          break;
        case "date-time":
          const dateTimeValue = localValue ? new Date(localValue) : new Date();
          contextMenu.setMenuContent(
            <DateTimePicker
              date={dateTimeValue}
              hideTime={false}
              onPick={({ date: newDate }) => {
                const utcDate = fromUserTimezone(newDate);
                setLocalValue(utcDate.toISOString());
                const dtWritePayload = writeKey
                  ? buildWritePayload(writeKey, utcDate.toISOString())
                  : { [columnKey]: utcDate.toISOString() };
                triggerBulkEditIfNeeded(utcDate.toISOString(), value, dtWritePayload);
                const modified = !shallowEqual(value, utcDate.toISOString());
                setIsModified(modified);
                const onBlurProps: CellBlurEvent = {
                  rowId,
                  columnKey,
                  original: value,
                  current: utcDate.toISOString(),
                  modified,
                  valid: validate?.({ cell: utcDate.toISOString() }) ?? true,
                  reset: handleReset,
                  writePayload: dtWritePayload,
                };
                onBlur?.(onBlurProps);
                setIsValid(validate?.({ cell: utcDate.toISOString() }) ?? true);
                onStopEdit?.();
                setIsEditing(false);
                contextMenu.setVisible(false);
              }}
            />,
          );
          break;
        case "integer":
          // No context menu for integer - just validate input
          break;
        case "decimal":
          break;
        case "currency":
          // No context menu for currency - handle formatting in input
          break;
        case "enum":
          const enumConfig = typeof inputType === "object" && inputType.type === "enum" ? inputType : null;
          if (enumConfig) {
            contextMenu.setMenuContent(
              <EnumMenu
                style={{ width: columnWidths[columnKey].value - 20, maxWidth: 200 }}
                options={enumConfig.options}
                onSet={(val) => {
                  setLocalValue(val);
                  // Trigger bulk edit logic immediately
                  const enumWritePayload = writeKey ? buildWritePayload(writeKey, val) : { [columnKey]: val };
                  triggerBulkEditIfNeeded(val, value, enumWritePayload);
                  // Trigger onBlur callbacks manually without using handleBlur
                  const modified = !shallowEqual(value, val);
                  setIsModified(modified);
                  const onBlurProps: CellBlurEvent = {
                    rowId,
                    columnKey,
                    original: value,
                    current: val,
                    modified,
                    valid: validate?.({ cell: val }) ?? true,
                    reset: handleReset,
                    writePayload: enumWritePayload,
                  };
                  onBlur?.(onBlurProps);
                  setIsValid(validate?.({ cell: val }) ?? true);
                  onStopEdit?.();
                  setIsEditing(false);
                  contextMenu.setVisible(false);
                }}
              />,
            );
          }
          break;
        case "array":
          const arrayConfig = typeof inputType === "object" && inputType.type === "array" ? inputType : null;
          if (arrayConfig) {
            const currentArray = Array.isArray(localValue) ? localValue : [];
            const readonlyArrayValues =
              arrayConfig.readonlyFromField && rowData?.[arrayConfig.readonlyFromField]
                ? [rowData[arrayConfig.readonlyFromField]]
                : [];
            contextMenu.setMenuContent(
              <ArrayMenu
                pickerType={arrayConfig.picker}
                selectedValues={currentArray}
                readonlyValues={readonlyArrayValues}
                onSet={(values) => {
                  setLocalValue(values);
                  const arrayWritePayload = writeKey ? buildWritePayload(writeKey, values) : { [columnKey]: values };
                  triggerBulkEditIfNeeded(values, value, arrayWritePayload);
                  const modified = !shallowEqual(value, values);
                  setIsModified(modified);
                  const onBlurProps: CellBlurEvent = {
                    rowId,
                    columnKey,
                    original: value,
                    current: values,
                    modified,
                    valid: true,
                    reset: handleReset,
                    writePayload: arrayWritePayload,
                  };
                  onBlur?.(onBlurProps);
                  onStopEdit?.();
                  setIsEditing(false);
                  contextMenu.setVisible(false);
                }}
                onCancel={() => {
                  contextMenu.setVisible(false);
                  onStopEdit?.();
                  setIsEditing(false);
                }}
              />,
            );
          }
          break;
        case "time_object":
          // Capture value at edit start to avoid re-render loops
          editingValueRef.current = localValue;
          contextMenu.setClickOutside(false);
          contextMenu.setMenuContent(
            <TimeEditorMenu
              value={editingValueRef.current}
              onSave={(timeData) => {
                const fullWritePayload = writeKey ? buildWritePayload(writeKey, timeData) : { [columnKey]: timeData };
                setLocalValue(timeData);
                triggerBulkEditIfNeeded(timeData, value, fullWritePayload);
                const modified = !shallowEqual(value, timeData);
                setIsModified(modified);
                const onBlurProps: CellBlurEvent = {
                  rowId,
                  columnKey,
                  original: value,
                  current: timeData,
                  modified,
                  valid: true,
                  reset: handleReset,
                  writePayload: fullWritePayload,
                };
                onBlur?.(onBlurProps);
                onStopEdit?.();
                setIsEditing(false);
                contextMenu.setVisible(false);
              }}
              onCancel={() => {
                contextMenu.setVisible(false);
                onStopEdit?.();
                setIsEditing(false);
              }}
            />,
          );
          break;
        case "rate_object":
          // Capture value at edit start to avoid re-render loops
          editingValueRef.current = localValue;
          // Parse rates if it's a JSON string
          const rateData =
            typeof editingValueRef.current === "string"
              ? (() => {
                  try {
                    return JSON.parse(editingValueRef.current);
                  } catch {
                    return {};
                  }
                })()
              : editingValueRef.current || {};
          contextMenu.setMenuContent(
            <RateEditorMenu
              value={rateData}
              onSave={(newRates) => {
                // For display: use snapshot if override is null, else use override
                const newValue = {
                  ...rateData,
                  billing_rate: newRates.billing_rate ?? rateData.billing_rate_snapshot,
                  payroll_rate: newRates.payroll_rate ?? rateData.payroll_rate_snapshot,
                };
                // Build write payload with overrides for meta_data
                // null = remove override (use snapshot), non-null = flat override
                const writePayloadValue = {
                  billing_rate_override: newRates.billing_rate, // null to remove, object to set
                  payroll_rate_override: newRates.payroll_rate, // null to remove, object to set
                };
                const fullWritePayload = writeKey
                  ? buildWritePayload(writeKey, writePayloadValue)
                  : { [columnKey]: newValue };

                setLocalValue(newValue);
                triggerBulkEditIfNeeded(newValue, value, fullWritePayload);
                const modified = !shallowEqual(value, newValue);
                setIsModified(modified);
                const onBlurProps: CellBlurEvent = {
                  rowId,
                  columnKey,
                  original: value,
                  current: newValue,
                  modified,
                  valid: true,
                  reset: handleReset,
                  writePayload: fullWritePayload,
                };
                onBlur?.(onBlurProps);
                onStopEdit?.();
                setIsEditing(false);
                contextMenu.setVisible(false);
              }}
              onCancel={() => {
                contextMenu.setVisible(false);
                onStopEdit?.();
                setIsEditing(false);
              }}
            />,
          );
          break;
        case "override_string": {
          if (typeof inputType !== "object" || inputType.type !== "override_string") break;
          editingValueRef.current = localValue;
          const osData =
            typeof editingValueRef.current === "string"
              ? (() => {
                  try {
                    return JSON.parse(editingValueRef.current);
                  } catch {
                    return {};
                  }
                })()
              : editingValueRef.current || {};
          const osOriginalKey = inputType.originalKey;
          const osOverrideKey = inputType.overrideKey;
          const osLabel = inputType.label;
          contextMenu.setMenuContent(
            <OverrideStringEditorMenu
              value={osData}
              label={osLabel}
              originalKey={osOriginalKey}
              overrideKey={osOverrideKey}
              onSave={(override) => {
                const newValue = {
                  ...osData,
                  [osOverrideKey]: override,
                };
                const writePayloadValue = {
                  [osOverrideKey]: override,
                };
                const fullWritePayload = writeKey
                  ? buildWritePayload(writeKey, writePayloadValue)
                  : { [columnKey]: newValue };

                setLocalValue(newValue);
                triggerBulkEditIfNeeded(newValue, value, fullWritePayload);
                const modified = !shallowEqual(value, newValue);
                setIsModified(modified);
                const onBlurProps: CellBlurEvent = {
                  rowId,
                  columnKey,
                  original: value,
                  current: newValue,
                  modified,
                  valid: true,
                  reset: handleReset,
                  writePayload: fullWritePayload,
                };
                onBlur?.(onBlurProps);
                onStopEdit?.();
                setIsEditing(false);
                contextMenu.setVisible(false);
              }}
              onCancel={() => {
                contextMenu.setVisible(false);
                onStopEdit?.();
                setIsEditing(false);
              }}
            />,
          );
          break;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      inputRef,
      offsetX,
      columnWidths,
      columnKey,
      inputType,
      localValue,
      value,
      settings.timeFormat,
      fromUserTimezone,
      triggerBulkEditIfNeeded,
      validate,
      rowId,
      onBlur,
      handleReset,
      onStopEdit,
      writeKey,
    ],
  );

  const handleFocus = useCallback(
    (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
      // Use cached layout instead of measuring
      const layout = measure(ref);
      if (!layout) return;

      const inputTypeStr = typeof inputType === "object" ? inputType.type : inputType;

      // Batch all state updates together to prevent multiple re-renders
      const isValidValue = validate?.({ cell: localValue }) ?? true;

      // Defer context menu setup to avoid blocking the UI thread
      rafRef.current = requestAnimationFrame(() => {
        // Only create context menu content for types that need it
        if (
          inputTypeStr === "boolean" ||
          inputTypeStr === "date" ||
          inputTypeStr === "date-time" ||
          inputTypeStr === "enum" ||
          inputTypeStr === "array" ||
          inputTypeStr === "time_object" ||
          inputTypeStr === "rate_object" ||
          inputTypeStr === "override_string"
        ) {
          createContextMenuContent(inputTypeStr, layout);
          contextMenu.setVisible(true);
        } else {
          contextMenu.setVisible(false);
        }
      });

      // Batch state updates
      setIsValid(isValidValue);
      if (!isValidValue) {
        setInvalid?.((prev) => prev?.add(`${rowIndex}-${columnIndex}`));
      } else {
        setInvalid?.((prev) => {
          const next = new Set(prev);
          next.delete(`${rowIndex}-${columnIndex}`);
          return next;
        });
      }
      onStartEdit?.();
      setIsEditing(editable);
      onFocus?.(e);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getCachedLayout,
      inputType,
      validate,
      localValue,
      createContextMenuContent,
      contextMenu,
      setInvalid,
      rowIndex,
      columnIndex,
      onStartEdit,
      editable,
      onFocus,
    ],
  );

  useEffect(() => {
    if (!isValid) {
      setInvalid?.((prev) => {
        return prev?.add(`${rowIndex}-${columnIndex}`);
      });
    } else {
      setInvalid?.((prev) => {
        const next = new Set(prev);
        next.delete(`${rowIndex}-${columnIndex}`);
        return next;
      });
    }
  }, [isValid, columnIndex, rowIndex, setInvalid]);

  // Handle time_object and rate_object cells - open menu directly on edit since there's no TextInput
  useEffect(() => {
    if (isEditing && typeof inputType === "object") {
      const type = inputType.type;
      if (type === "time_object" || type === "rate_object" || type === "override_string") {
        const layout = measure(ref);
        if (layout) {
          createContextMenuContent(type, layout);
          contextMenu.setVisible(true);
          onStartEdit?.();
          // Set up dismiss handler after menu is visible (use setTimeout to avoid setState during render)
          setTimeout(() => {
            contextMenu.onDismiss?.(() => {
              setIsEditing(false);
              onStopEdit?.();
            });
          }, 0);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when isEditing changes, not when createContextMenuContent is recreated
  }, [isEditing]);

  const handleBlur = (e?: NativeSyntheticEvent<TextInputFocusEventData>) => {
    const inputTypeStr = typeof inputType === "object" ? inputType.type : inputType;
    let processedValue = localValue;

    // Apply lazy formatting for integer and currency
    if (inputTypeStr === "integer" && localValue) {
      processedValue = parseIntegerInput(localValue);
    } else if (inputTypeStr === "currency" && localValue) {
      processedValue = parseCurrencyInput(localValue);
    } else if (inputTypeStr === "percentage" && localValue) {
      const num = parseFloat(localValue.replace(/[^\d.-]/g, ""));
      processedValue = isNaN(num) ? "" : num.toString();
    }

    // Only update localValue if it actually changed after processing
    if (processedValue !== localValue) {
      setLocalValue(processedValue);
    }

    const current = processedValue;
    const original = value;
    const modified = !shallowEqual(value, processedValue);
    const valid = validate?.({ cell: processedValue }) ?? true;

    setIsModified(modified);
    setIsValid(valid);

    const fullWritePayload = writeKey ? buildWritePayload(writeKey, current) : { [columnKey]: current };

    // For handleBlur, only trigger bulk update if actually modified (existing behavior)
    if (modified && isSelected && selectedRowIds && selectedRowIds.length > 1 && triggerBulkUpdate) {
      triggerBulkUpdate(columnKey, current, selectedRowIds, fullWritePayload);
    }

    const onBlurProps: CellBlurEvent = {
      rowId,
      columnKey,
      original,
      current,
      modified,
      valid,
      reset: handleReset,
      writePayload: fullWritePayload,
    };
    onBlur?.(onBlurProps);

    onStopEdit?.();
    setIsEditing(false);
  };

  const animatedStyle = useAnimatedStyle(() => {
    return { width: columnWidths.value[columnKey] ?? savedWidth };
  }, []);

  const handleChangeText = useCallback(
    (s: string) => {
      const inputTypeStr = typeof inputType === "object" ? inputType.type : inputType;

      // Don't update if invalid characters
      if (inputTypeStr === "integer" && !inputPatterns.integer.test(s)) return;
      if (inputTypeStr === "currency" && !inputPatterns.currency.test(s)) return;
      if (inputTypeStr === "percentage" && !inputPatterns.percentage.test(s)) return;

      setLocalValue(s);
      const valid = validate?.({ cell: s }) ?? true;
      setIsValid(valid);

      // NEW: report dirty state immediately so save/discard shows while typing
      const modified = !shallowEqual(value, s);
      setIsModified(modified);

      const fullWritePayload = writeKey ? buildWritePayload(writeKey, s) : { [columnKey]: s };
      onBlur?.({
        rowId,
        columnKey,
        original: value,
        current: s,
        modified,
        valid,
        reset: handleReset,
        writePayload: fullWritePayload,
      });
    },
    [inputType, validate, value, writeKey, columnKey, rowId, onBlur, handleReset],
  );

  alignment = alignment ? alignment : "left";

  const handleTextLayout = useCallback((event: LayoutChangeEvent) => {
    setTextWidth(event.nativeEvent.layout.width);
  }, []);

  const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  useEffect(() => {
    if (textWidth > 0 && containerWidth - overflowBuffer > 0) {
      const isClipping = textWidth > containerWidth - overflowBuffer;
      setIsOverflowing(isClipping);
      setColumnClipping((prev) => ({
        ...prev,
        [rowIndex]: {
          ...(prev[rowIndex] ?? {}),
          [columnKey]: isClipping,
        },
      }));
    }
  }, [textWidth, containerWidth, rowIndex, columnKey, setColumnClipping]);

  // Determine dot color for boolean values (handle both boolean and string representations)
  const booleanDotColor =
    typeof inputType === "object" && inputType.type === "boolean"
      ? localValue === true || localValue === "true"
        ? resolveColor(inputType.trueColor, colors)
        : localValue === false || localValue === "false"
          ? resolveColor(inputType.falseColor, colors)
          : "transparent"
      : undefined;

  // Determine dot color for enum values
  const enumDotColor =
    typeof inputType === "object" && inputType.type === "enum"
      ? resolveColor(inputType.options?.find((opt) => opt.value === localValue)?.color, colors)
      : undefined;

  const cellDotColor = booleanDotColor || enumDotColor;

  // Memoize display value to avoid recalculating on every render
  const displayValue = useMemo(() => {
    const inputTypeStr = typeof inputType === "object" ? inputType.type : inputType;
    const currencyConfig = typeof inputType === "object" && inputType.type === "currency" ? inputType : null;

    if (inputTypeStr === "currency" && localValue) {
      const symbol = currencyConfig?.symbol || "$";
      return formatCurrency(localValue, symbol);
    }
    if (inputTypeStr === "percentage") {
      const num = parseFloat(localValue);
      return isNaN(num) ? "0%" : `${num}%`;
    }
    if (inputTypeStr === "linked_record" && localValue && typeof localValue === "object") {
      const lr = typeof inputType === "object" && inputType.type === "linked_record" ? inputType : {};
      return formatLinkedRecord(localValue, lr);
    }
    return `${localValue}`;
  }, [inputType, localValue]);

  let defaultCellView = (cursorType: string = "auto") => {
    return (
      <View>
        <ThemedText
          onLayout={handleTextLayout}
          numberOfLines={1}
          style={[
            styles.text,
            { color: colors.text, cursor: cursorType },
            !isValid && { color: colors.red.text },
            isHovered && !editable && readonlyCallback && { textDecorationLine: "underline" },
          ]}
        >
          {cellDotColor && <StatusDot color={enumDotColor!} />}
          {displayValue}
        </ThemedText>
      </View>
    );
  };

  const arrayConfig = typeof inputType === "object" && inputType.type === "array" ? inputType : null;

  const arrayDisplayMap = useMemo(() => {
    if (!arrayConfig?.options) return undefined;
    return arrayConfig.options.reduce(
      (acc, opt) => {
        acc[opt.value] = opt.label;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [arrayConfig?.options]);

  const arrayColorMap = useMemo(() => {
    if (!arrayConfig?.options) return undefined;
    return arrayConfig.options.reduce(
      (acc, opt) => {
        if (opt.color) acc[opt.value] = opt.color;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [arrayConfig?.options]);

  return (
    <Animated.View
      ref={ref}
      onLayout={handleWrapperLayout}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      style={[styles.container, { width: 300 }, animatedStyle]}
    >
      <Animated.View
        onLayout={handleContainerLayout}
        style={[styles.cell, { borderColor: colors.border }, style]}
        ref={cellRef}
        onPointerEnter={() => {
          setIsHovered(true);
          if (!isOverflowing || !toolTip) return;
          isHovering.current = true;
          toolTip.setToolTipContent(
            <View
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
                borderWidth: 1,
                borderRadius: 10,
                paddingHorizontal: 8,
                paddingVertical: 4,
              }}
            >
              <ThemedText style={{ fontSize: 12 }}>{displayValue}</ThemedText>
            </View>,
          );
          cellRef.current?.measureInWindow((x: number, y: number) => {
            if (!isHovering.current) return;
            toolTip.setLocation({ x, y: y - 38 } as any);
            toolTip.setVisible(true);
          });
        }}
        onPointerLeave={() => {
          setIsHovered(false);
          isHovering.current = false;
          if (toolTip) toolTip.setVisible(false);
        }}
        tabIndex={0}
      >
        <Animated.View
          onPointerUp={() => setIsEditing(true && editable)}
          style={[
            styles.dataWrapper,
            isHovered && editable && { borderColor: colors.placeholder },
            isEditing && { borderColor: colors.primary, borderStyle: "solid" },
            !shallowEqual(localValue, value) && { borderColor: colors.amber.border, borderStyle: "solid" },
          ]}
        >
          {(() => {
            switch (true) {
              case typeof inputType === "object" && inputType.type === "time_object":
                return <TimeBar value={localValue} />;
              case typeof inputType === "object" && inputType.type === "rate_object":
                return <RateBar value={localValue} />;
              case typeof inputType === "object" && inputType.type === "override_string":
                return (
                  <OverrideStringBar
                    value={localValue}
                    originalKey={inputType.originalKey}
                    overrideKey={inputType.overrideKey}
                  />
                );
              case isEditing:
                return (
                  <IsEditingCell
                    inputType={inputType}
                    inputRef={inputRef}
                    localValue={localValue}
                    isValid={isValid}
                    editable={editable}
                    handleFocus={handleFocus}
                    handleChangeText={handleChangeText}
                    handleBlur={handleBlur}
                    handleTextLayout={handleTextLayout}
                  />
                );
              case typeof inputType === "object" && inputType.type === "array" && Array.isArray(localValue):
                return (
                  <ArrayChips
                    values={localValue}
                    chipColor={inputType.chipColor}
                    displayMap={inputType.options?.reduce(
                      (acc, opt) => {
                        acc[opt.value] = opt.label;
                        return acc;
                      },
                      {} as Record<string, string>,
                    )}
                    colorMap={inputType.options?.reduce(
                      (acc, opt) => {
                        if (opt.color) acc[opt.value] = opt.color;
                        return acc;
                      },
                      {} as Record<string, string>,
                    )}
                    readonlyValues={
                      inputType.readonlyFromField && rowData?.[inputType.readonlyFromField]
                        ? [rowData[inputType.readonlyFromField]]
                        : []
                    }
                    readonlyLabel="Location"
                  />
                );
              case typeof inputType === "object" && (inputType.type === "enum" || inputType.type === "linked_record"):
                return defaultCellView("pointer");
              case inputType === "currency":
                return defaultCellView("text");
              case inputType === "string":
                return defaultCellView("text");
              case inputType === "date-time" || inputType === "date":
                return defaultCellView("default");
              default:
                return defaultCellView();
            }
          })()}
          {isModified && !shallowEqual(localValue, value) && (
            <Button
              icon="undo-variant"
              iconSize={14}
              style={styles.reset}
              onPress={() => {
                setLocalValue(value);
                setIsValid(validate?.({ cell: value }) ?? true);
                const original = value;
                const modified = !shallowEqual(value, localValue);
                setIsModified(modified);
                const valid = isValid;
                const onBlurProps: CellBlurEvent = {
                  rowId,
                  columnKey,
                  original: value,
                  current: original,
                  modified: false,
                  valid,
                  reset: handleReset,
                  writePayload: writeKey ? buildWritePayload(writeKey, original) : { [columnKey]: original },
                };
                onBlur?.(onBlurProps);
              }}
            />
          )}
        </Animated.View>
      </Animated.View>
    </Animated.View>
  );
}

// Memoize component to prevent unnecessary re-renders
export const TableCell = React.memo(TableCellComponent, (prevProps, nextProps) => {
  // Compare selectedRowIds length (not reference) for bulk edit detection
  const prevLength = prevProps.selectedRowIds?.length ?? 0;
  const nextLength = nextProps.selectedRowIds?.length ?? 0;

  // Custom comparison function - only re-render if these props change
  return (
    prevProps.value === nextProps.value &&
    prevProps.editable === nextProps.editable &&
    prevProps.isSelected === nextProps.isSelected &&
    prevLength === nextLength && // Re-render when selection count changes
    prevProps.rowIndex === nextProps.rowIndex &&
    prevProps.columnIndex === nextProps.columnIndex &&
    prevProps.inputType === nextProps.inputType
  );
});

const styles = StyleSheet.create({
  container: {
    // zIndex: -1,
    // elevation: -1,
    // overflow: "visible",
  },
  cell: {
    flex: 1,
    alignItems: "flex-start",
    borderRightWidth: 1,
    overflowX: "visible",
    position: "relative",
    height: 32,
    padding: 4,
  },
  dataWrapper: {
    flex: 1,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
    borderColor: "transparent",
    borderStyle: "dashed",
    width: "100%",
  },
  text: {
    position: "absolute",
    fontSize: 16,
    borderRadius: 8,
    paddingHorizontal: 4,
    height: 28,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  input: {
    top: -2,
    position: "absolute",
    fontSize: 16,
    borderRadius: 8,
    paddingHorizontal: 4,
    height: 28,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  buttons: {
    flexDirection: "row",
    top: 2,
  },
  menu: {
    minWidth: 400,
    maxWidth: 400,
    width: "auto",
    left: 0,
    top: 30,
  },
  reset: {
    position: "absolute",
    backgroundColor: "transparent",
    right: 0,
    minHeight: 26,
    minWidth: 26,
    borderWidth: 0,
    borderRadius: 99,
  },
});
